import {
  every,
  isEmpty,
  keys,
  map,
  orderBy,
  pick,
  toLower,
  uniq,
  values,
  words
} from "lodash";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import CenteredLoading from "../../../components/CenteredLoading";
import ContactComponent from "../../../components/ContactComponent";
import FeatureExplanation from "../../../components/FeatureExplanation";
import FilterLabels from "../../../components/labels/FilterLabels";
import Labels from "../../../components/labels/Labels";
import { useAuthentication } from "../../../context/authentication";
import { useCommissionReports } from "../../../context/commissionReports";
import { useContacts } from "../../../context/contacts";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useInstalls } from "../../../context/installs";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useMembers } from "../../../context/members/MembersProvider";
import { useToast } from "../../../context/toast";
import { useWorkspaces } from "../../../context/workspaces";
import { Entity } from "../../../utils/constants";
import { downloadCsv } from "../../../utils/csv";
import { formatDateToLocale } from "../../../utils/dates";
import { Contact } from "../../../utils/types";
import useCurrency from "../../../utils/useCurrency";
import useDebounce from "../../../utils/useDebounce";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import EntityDataTable from "../common/EntityDataTable";
import MemberCell from "../common/MemberCell";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import TableSearchInput from "../common/TableSearchInput";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import { isActiveInstall } from "../InstallsData/utils";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";
import ContactActionsButton from "./ContactActionsButton";
import ContactFormSidebar from "./ContactFormSidebar";
import ImportContactsModal from "./ImportContactsModal";

interface Props {}

const ContactsData: React.FC<Props> = () => {
  const { setModalWarningConfig } = useToast();
  const { wrapActionWithAuth } = useAuthentication();
  const { contacts, removeContacts } = useContacts();
  const { getCurrencyTitle } = useCurrency();
  const { installs } = useInstalls();
  const {
    currentWorkspaceId,
    workspaces,
    currentWorkspaceIsLocked,
    currentWorkspaceAllowedForCommunityPlan
  } = useWorkspaces();
  const { membersLimitExceeded } = useMembers();
  const { labels, linkedLabels } = useLabels();
  const { setGenerateCommissionReportSidebarConfig } = useCommissionReports();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable((state) => state.sortOrder[Entity.contact]);
  const searchInput = useDataTable(
    (state) => state.searchInput[Entity.contact]
  );
  const [showImportContactsModal, setShowImportContactsModal] = useState(false);
  const [importContactsModalErrorMessage, setImportContactsModalErrorMessage] =
    useState<string | null>(null);
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);
  const [currentlyEditedContact, setCurrentlyEditedContact] = useState<
    Contact | undefined
  >();
  const { getIsReady } = useLoading();

  const toggleEditContactForm = useCallback(
    (contact: Contact | undefined) => {
      if (openContactFormSidebar) {
        setCurrentlyEditedContact(undefined);
        setOpenContactFormSidebar(false);
      } else {
        setCurrentlyEditedContact(contact);
        setOpenContactFormSidebar(true);
      }
    },
    [openContactFormSidebar]
  );

  const isContactHosting = useCallback(
    ({ linked_location_ids }: Contact) => {
      const linkedActiveInstalls = values(installs)
        .filter(isActiveInstall)
        .filter(({ location_uuid }) =>
          linked_location_ids.includes(location_uuid)
        );

      return !isEmpty(linkedActiveInstalls);
    },
    [installs]
  );

  const smartLabelFilters: {
    [key: string]: (contact: Contact) => boolean;
  } = useMemo(
    () => ({
      hosting: isContactHosting
    }),
    [isContactHosting]
  );

  const filteredContactLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.workspaceContacts],
        labels,
        smartLabelFilters,
        Entity.contact
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredContacts = useMemo(() => {
    let filteredList = values(contacts).filter((contact) => {
      if (
        !isEmpty(filteredContactLabels) &&
        !every(filteredContactLabels, ({ uuid }) => {
          const smartFilterFunction = (smartLabelFilters as any)[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(contact)
            : (linkedLabels[contact.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (debouncedSearchInput) {
        const searchInputWords = words(debouncedSearchInput).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(contact.name).includes(word) ||
            toLower(contact.email || "").includes(word) ||
            toLower(contact.phone || "").includes(word)
        );
      }
      return true;
    });

    if (sortOrder) {
      filteredList = orderBy(
        filteredList,
        [sortOrder.iteratee],
        [sortOrder.order]
      );
    }

    return filteredList;
  }, [
    contacts,
    sortOrder,
    filteredContactLabels,
    debouncedSearchInput,
    smartLabelFilters,
    linkedLabels
  ]);

  const properties: { [key: string]: Property<Contact> } = useMemo(
    () => ({
      name: {
        key: "name",
        title: "Contacts",
        style: { minWidth: "18rem" },
        sortIteratee: "name",
        renderContent: (contact) => (
          <Link
            className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
            to={`/contacts/${contact.uuid}/info`}
          >
            <ContactComponent contactId={contact.uuid} withoutLabels />
          </Link>
        )
      },
      labels: {
        key: "labels",
        title: "Labels",
        style: { minWidth: "26rem" },
        renderContent: (contact) => (
          <Labels
            entity={Entity.contact}
            linkedUuid={contact.uuid}
            filterKey={LabelFilterKey.workspaceContacts}
          />
        )
      },
      email: {
        key: "email",
        title: "Email",
        style: { minWidth: "20rem" },
        sortIteratee: "email",
        renderContent: (contact) => contact.email
      },
      phone: {
        key: "phone",
        title: "Phone",
        style: { minWidth: "20rem" },
        sortIteratee: "phone",
        renderContent: (contact) => contact.phone
      },
      vat: {
        key: "vat",
        title: "VAT",
        style: { minWidth: "20rem" },
        sortIteratee: "vat",
        renderContent: (contact) => contact.vat
      },
      payment_methods: {
        key: "payment_methods",
        title: "Payment methods",
        style: { minWidth: "20rem" },
        sortIteratee: "payment_methods",
        renderContent: (contact) =>
          isEmpty(contact.payment_methods)
            ? null
            : map(
                uniq(map(contact.payment_methods, "currency")),
                getCurrencyTitle
              ).join(", ")
      },
      linkedLocationsCount: {
        key: "linkedLocationsCount",
        title: "Locations",
        style: { minWidth: "8rem" },
        renderContent: (contact) => contact.linked_location_ids.length
      },
      hosting: {
        key: "hosting",
        title: "Hosting",
        renderContent: (contact) => (isContactHosting(contact) ? "Yes" : "No")
      },
      created_at: {
        key: "created_at",
        title: "Added",
        style: { minWidth: "13rem" },
        sortIteratee: "created_at",
        renderContent: (contact) => (
          <MemberCell
            memberUuid={contact.created_by}
            subtitle={formatDateToLocale(contact.created_at)}
          />
        )
      },
      updated_at: {
        key: "updated_at",
        title: "Updated",
        style: { minWidth: "13rem" },
        sortIteratee: "updated_at",
        renderContent: (contact) =>
          contact.updated_by ? (
            <MemberCell
              memberUuid={contact.updated_by}
              subtitle={formatDateToLocale(contact.updated_at)}
            />
          ) : (
            "-"
          )
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (contact) => (
          <ContactActionsButton
            contact={contact}
            toggleEditContactForm={toggleEditContactForm}
          />
        )
      }
    }),
    [getCurrencyTitle, isContactHosting, toggleEditContactForm]
  );

  const isLoading = useMemo(
    () => isEmpty(contacts) && !getIsReady([LoadingKey.workspaceContacts]),
    [contacts, getIsReady]
  );

  const renderPageContent = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) {
      return <CenteredLoading className="sm:h-full h-96" />;
    }

    if (isEmpty(contacts)) {
      return (
        <FeatureExplanation
          title="Contacts"
          paragraphs={contactsExplanationParagraphs}
        >
          <button
            id="add-contact-button"
            type="button"
            className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => setOpenContactFormSidebar(true))}
          >
            Add contact
          </button>
          <button
            type="button"
            className="mt-6 inline-flex items-center justify-center ml-3 px-3 py-1.5 border border-hotspotty-500 text-sm font-medium rounded-md shadow-sm text-hotspotty-500 hover:bg-hotspotty-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => setShowImportContactsModal(true))}
          >
            Import Contacts
          </button>
        </FeatureExplanation>
      );
    }

    return (
      <>
        <div className="p-4 ">
          <TableSearchInput
            entity={Entity.contact}
            placeholder="Search contacts by name, email or phone"
          />

          <div className="mt-2 flex items-center">
            <FilterLabels
              className="flex-1"
              entity={Entity.contact}
              filteredLabels={filteredContactLabels}
              smartLabels={keys(smartLabelFilters).map((key) =>
                getSmartLabelFromKey(key, Entity.contact)
              )}
              filterKey={LabelFilterKey.workspaceContacts}
            />

            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                },
                {
                  text: "Import contacts",
                  onClick: () => {
                    setShowImportContactsModal(true);
                  }
                },
                {
                  disabled: isEmpty(filteredContacts),
                  onClick: () => {
                    downloadCsv(
                      filteredContacts.map((contact) =>
                        pick(contact, [
                          "uuid",
                          "created_at",
                          "updated_at",
                          "name",
                          "email",
                          "phone"
                        ])
                      ),
                      `${
                        workspaces[currentWorkspaceId as string].name
                      } contacts ${moment().utc().format("YYYY_MM_DD_HH_mm")}`
                    );
                  },
                  text: `Export ${
                    filteredContactLabels.length ? "filtered" : "all"
                  } contacts`
                },
                {
                  onClick: () => {
                    setModalWarningConfig({
                      open: true,
                      resolve: () => {
                        removeContacts(map(filteredContacts, "uuid"));
                      }
                    });
                  },
                  text: `Remove ${
                    filteredContactLabels.length ? "filtered" : "all"
                  } contacts`,
                  disabled: isEmpty(filteredContacts)
                }
              ]}
            />

            <button
              id="add-contact-button"
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={wrapActionWithAuth(() =>
                setOpenContactFormSidebar(true)
              )}
            >
              Add contact
            </button>
          </div>
        </div>

        <EntityDataTable<Contact>
          paginationKey={PaginationKey.workspaceContacts}
          entity={Entity.contact}
          items={filteredContacts}
          properties={properties}
          selectionActions={[
            {
              title: "Generate commission report",
              callback: (
                selectedContacts: Contact[],
                setSelectedContacts: React.Dispatch<
                  React.SetStateAction<Contact[]>
                >
              ) => {
                setGenerateCommissionReportSidebarConfig({
                  show: true,
                  contacts: selectedContacts
                });
              }
            },
            {
              title: "Remove contacts",
              callback: (
                selectedContacts: Contact[],
                setSelectedContacts: React.Dispatch<
                  React.SetStateAction<Contact[]>
                >
              ) => {
                setModalWarningConfig({
                  open: true,
                  resolve: () => {
                    removeContacts(map(selectedContacts, "uuid"));
                    setSelectedContacts([]);
                  }
                });
              }
            }
          ]}
        />
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contacts,
    currentWorkspaceId,
    currentWorkspaceIsLocked,
    filteredContactLabels,
    filteredContacts,
    isLoading,
    membersLimitExceeded,
    properties,
    removeContacts,
    setGenerateCommissionReportSidebarConfig,
    setModalWarningConfig,
    smartLabelFilters,
    workspaces,
    wrapActionWithAuth
  ]);

  return (
    <>
      <ContactFormSidebar
        contact={currentlyEditedContact}
        open={openContactFormSidebar}
        toggleEditContactForm={toggleEditContactForm}
      />

      <TablePropertiesSidebar<Contact>
        entity={Entity.contact}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {importContactsModalErrorMessage && (
        <AlertMessage
          className="mb-6"
          type="error"
          title="Import Contacts"
          message={importContactsModalErrorMessage}
          onClose={() => setImportContactsModalErrorMessage(null)}
        />
      )}

      <ImportContactsModal
        open={showImportContactsModal}
        setOpen={setShowImportContactsModal}
        setErrorMessage={setImportContactsModalErrorMessage}
      />

      {renderPageContent}
    </>
  );
};

export default ContactsData;

//
// Utils
//

export const contactsExplanationParagraphs = [
  <>
    Contacts represent anyone facilitating your{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/hotspots"
    >
      hotspot
    </Link>{" "}
    deployment and optionally{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/payments"
    >
      gets paid
    </Link>{" "}
    for their added value.
  </>,
  <>
    This can range from someone providing{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/locations"
    >
      locations
    </Link>{" "}
    to be assessed, to hosts or even regional deployment partners.
  </>,
  <>
    You can link these contacts to their{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/locations"
    >
      locations
    </Link>
    , so that{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/installs"
    >
      installs
    </Link>{" "}
    can link them to the hotspot earnings.
  </>
];
